import { MyFavoritesItemStatusTab } from '@/pages/MyItems/MyFavorites/types/MyFavoritesPage.types';
import * as similarItemsGroupsSearchApi from '@/redux/api/similarItemsGroups';
import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken, getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getIsBot } from '@/redux/modules/analytics';
import { getClientIpAddress } from '@/redux/modules/browser';
import { getDeployment } from '@/redux/modules/config';
import { fetchSavedItems } from '@/redux/modules/item/saved/savedItems.actions';
import { getSavedItems as getSavedItemsApi } from '@/redux/modules/item/saved/savedItems.api';
import { fetchRecentItemsIfNeeded } from '@/redux/modules/recentItem';
import { SimilarItemsGroupsApiRow } from '@/redux/modules/similarItemsGroups/similarItemsGroups.types';
import { GlobalState } from '@/redux/store';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';

import {
    shouldUseSavedItemSeeds,
    getRecommendedSimilarItemsSavedSeedItemIds,
    getRecommendedSimilarItemsSeedItemIds,
    getRecommendedSimilarItemsViewedSeedItemIds,
    shouldFetchRecommendedSimilarItems,
} from './recommendedSimilarItems.selectors';
import {
    FetchRecommendedSimilarItemsPayload,
    FetchRecommendedSimilarItemsRequest,
    RECOMMENDED_SIMILAR_ITEMS_LIMIT,
    RECOMMENDED_SIMILAR_ITEMS_SEED_LIMIT,
} from './recommendedSimilarItems.types';

const SAVED_ITEMS_PAGE_SIZE = 24;

const fetchSavedSeedItemIdsForRecommendations = async (
    request: FetchRecommendedSimilarItemsRequest,
    getState: () => GlobalState
) => {
    const currentState = getState();
    let savedSeedItemIds = getRecommendedSimilarItemsSavedSeedItemIds(currentState, request);

    if (request.skipSavedItemsFetch && savedSeedItemIds.length === 0) {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        if (authToken && bidderId) {
            const { payload } = await getSavedItemsApi({
                authToken,
                bidderId,
                deployment,
                query: {
                    page: 1,
                    pageSize: SAVED_ITEMS_PAGE_SIZE,
                    sort: SearchSortAndDirection.SavedMostRecent,
                    status: MyFavoritesItemStatusTab.Available,
                },
            });

            savedSeedItemIds = payload.items.slice(0, RECOMMENDED_SIMILAR_ITEMS_SEED_LIMIT).map((item) => item.itemId);
        }
    }

    return savedSeedItemIds;
};

const fetchRecommendedSimilarItemsThunk = createAsyncThunk<
    FetchRecommendedSimilarItemsPayload,
    FetchRecommendedSimilarItemsRequest | undefined
>(
    'la/ui/recommendedSimilarItems/fetchRecommendedSimilarItems',
    async (request = {}, { dispatch, getState }) => {
        const { shouldUseLastTwoSavedLots, shouldUseLastTwoViewedLots, skipSavedItemsFetch } = request;

        if (shouldUseLastTwoSavedLots && !skipSavedItemsFetch) {
            await dispatch(
                fetchSavedItems({
                    page: 1,
                    pageSize: SAVED_ITEMS_PAGE_SIZE,
                    sort: SearchSortAndDirection.SavedMostRecent,
                    status: MyFavoritesItemStatusTab.Available,
                })
            );
        }

        const resolvedSavedSeedItemIds = shouldUseLastTwoSavedLots
            ? await fetchSavedSeedItemIdsForRecommendations(request, getState)
            : [];
        const shouldUseViewedSeedItemIds =
            shouldUseLastTwoViewedLots ||
            (shouldUseLastTwoSavedLots && !shouldUseSavedItemSeeds(resolvedSavedSeedItemIds, request));

        if (shouldUseViewedSeedItemIds) {
            await dispatch(fetchRecentItemsIfNeeded());
        }

        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const ipAddress = getClientIpAddress(state);
        const isBot = getIsBot(state);
        let seedItemIds: number[];

        if (shouldUseLastTwoViewedLots) {
            seedItemIds = getRecommendedSimilarItemsViewedSeedItemIds(state);
        } else if (shouldUseLastTwoSavedLots) {
            seedItemIds = shouldUseSavedItemSeeds(resolvedSavedSeedItemIds, request)
                ? resolvedSavedSeedItemIds
                : getRecommendedSimilarItemsViewedSeedItemIds(state);
        } else {
            seedItemIds = getRecommendedSimilarItemsSeedItemIds(state, request);
        }

        const responses = await Promise.all(
            seedItemIds.map((itemId) =>
                similarItemsGroupsSearchApi.fetchSimilarItemsGroups({
                    authToken,
                    deployment,
                    ipAddress,
                    isBot,
                    itemId,
                })
            )
        );

        const itemIds = [
            ...new Set(
                responses.flatMap(({ payload }) =>
                    (payload as SimilarItemsGroupsApiRow[]).flatMap((row) => row.items.map((item) => item.itemId))
                )
            ),
        ]
            .filter((itemId) => !seedItemIds.includes(itemId))
            .slice(0, RECOMMENDED_SIMILAR_ITEMS_LIMIT);

        return {
            actionTime: Date.now(),
            itemIds,
            seedItemIds,
        };
    },
    {
        condition: (request, { getState }) =>
            Boolean(request?.forceUpdate) || shouldFetchRecommendedSimilarItems(getState(), request ?? {}),
    }
);

export const fetchRecommendedSimilarItems = Object.assign(
    (request?: FetchRecommendedSimilarItemsRequest) => fetchRecommendedSimilarItemsThunk(request),
    fetchRecommendedSimilarItemsThunk
);
